import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "@/lib/axiosConfig";
import {
  ImportAnalyseResult,
  ImportAnalysisReport,
  ImportDecision,
  ImportDecisionAnswer,
  ImportDecisionGroup,
  ImportDecisionState,
  ImportField,
  ImportJob,
  ImportProgress,
  ImportRowReport,
  ImportTable,
  ImportTemplateProfile,
  ImportWriteMode,
  isTerminal,
} from "@/lib/import/import-types";

/**
 * Client de l'import de donnees.
 *
 * URL **relative sans slash initial** : `axiosConfig` reecrit `baseURL` a chaque
 * requete depuis la region active, et injecte `Authorization` et `Instance`.
 */
const baseUrl = "back/api/data-import";

// ------------------------------------------------------------------ catalogue

const getImportFields = async (
  table: ImportTable,
  lang?: string,
): Promise<ImportField[]> => {
  const { data } = await api.get<ImportField[]>(`${baseUrl}/${table}/fields`, {
    params: lang ? { lang } : undefined,
  });
  return data;
};

export const useImportFields = (table?: ImportTable, lang?: string) =>
  useQuery({
    queryKey: ["import", "fields", table, lang],
    queryFn: () => getImportFields(table as ImportTable, lang),
    enabled: !!table,
    // Le catalogue ne change qu'au deploiement ou au renommage d'un libelle.
    staleTime: 5 * 60 * 1000,
  });

// --------------------------------------------------------------------- modele

export interface DownloadTemplateDto {
  table: ImportTable;
  profile?: ImportTemplateProfile;
  lang?: string;
  withExample?: boolean;
  /** modele personnalise : les cles sont validees par le serveur */
  fieldKeys?: string[];
}

const downloadTemplate = (dto: DownloadTemplateDto) =>
  api.get(`${baseUrl}/${dto.table}/template`, {
    responseType: "blob",
    params: {
      ...(dto.profile ? { profile: dto.profile } : {}),
      ...(dto.lang ? { lang: dto.lang } : {}),
      ...(dto.withExample === false ? { withExample: "false" } : {}),
      ...(dto.fieldKeys?.length ? { fieldKeys: dto.fieldKeys.join(",") } : {}),
    },
  });

export const useDownloadImportTemplate = () =>
  useMutation({ mutationFn: downloadTemplate });

/**
 * Declenche le telechargement d'un blob recu.
 *
 * Reprend l'idiome de `export.tsx` : lire le nom dans `content-disposition`,
 * creer une URL objet, cliquer, **puis revoquer** l'URL.
 */
export function triggerBlobDownload(
  response: { data: unknown; headers: Record<string, unknown> },
  fallbackFilename: string,
): void {
  const blob = response.data as Blob;
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

// -------------------------------------------------------------------- analyse

export interface AnalyseImportFileDto {
  table: ImportTable;
  file: File;
  lang?: string;
  /** feuille choisie, si le classeur en contenait plusieurs */
  sheet?: string;
  /** progression du transfert, 0..100 */
  onUploadProgress?: (percent: number) => void;
}

const analyseImportFile = async ({
  table,
  file,
  lang,
  sheet,
  onUploadProgress,
}: AnalyseImportFileDto): Promise<ImportAnalyseResult> => {
  const form = new FormData();
  form.append("file", file);
  if (lang) form.append("lang", lang);
  if (sheet) form.append("sheet", sheet);

  const { data } = await api.post<ImportAnalyseResult>(
    `${baseUrl}/${table}/analyse`,
    form,
    {
      onUploadProgress: (event) =>
        onUploadProgress?.(
          event.total ? Math.round((event.loaded / event.total) * 100) : 0,
        ),
    },
  );
  return data;
};

export const useAnalyseImportFile = () =>
  useMutation({ mutationFn: analyseImportFile });

/**
 * Message d'erreur exploitable a partir d'une reponse de l'API.
 *
 * L'API renvoie `{ code, message }` sur les refus previsibles (fichier
 * corrompu, .xls, trop de lignes) : on privilegie ce message, deja redige pour
 * l'utilisateur, plutot qu'un texte generique.
 */
export function extractImportErrorMessage(error: unknown): {
  code?: string;
  message?: string;
} {
  const response = (
    error as { response?: { data?: { code?: string; message?: string } } }
  )?.response;
  const data = response?.data;
  if (!data) return {};
  return { code: data.code, message: data.message };
}

// ------------------------------------------------------------------ job

const getImportJob = async (jobId: string): Promise<ImportJob> =>
  (await api.get<ImportJob>(`${baseUrl}/job/${jobId}`)).data;

export const useImportJob = (jobId?: string) =>
  useQuery({
    queryKey: ["import", "job", jobId],
    queryFn: () => getImportJob(jobId as string),
    enabled: !!jobId,
  });

/** Détail des fiches liées à créer, par type : liste des noms à créer. */
const getImportToCreateSummary = async (
  jobId: string,
): Promise<{ byType: Record<string, string[]> }> =>
  (await api.get<{ byType: Record<string, string[]> }>(
    `${baseUrl}/job/${jobId}/to-create-summary`,
  )).data;

export const useImportToCreateSummary = (jobId?: string) =>
  useQuery({
    queryKey: ["import", "toCreateSummary", jobId],
    queryFn: () => getImportToCreateSummary(jobId as string),
    enabled: !!jobId,
  });

const getImportProgress = async (jobId: string): Promise<ImportProgress> =>
  (await api.get<ImportProgress>(`${baseUrl}/job/${jobId}/progress`)).data;

/**
 * Interroge la progression a la **cadence dictee par le serveur**.
 *
 * `retryAfterMs` vaut 0 sur un statut terminal, ce qui arrete la boucle : le
 * client n'a pas a deviner quand s'arreter. Noter que `queryClient` desactive
 * globalement `refetchOnWindowFocus`, donc cet intervalle est le seul moteur.
 */
export const useImportProgress = (jobId?: string, enabled = true) =>
  useQuery({
    queryKey: ["import", "progress", jobId],
    queryFn: () => getImportProgress(jobId as string),
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 1500;
      return data.retryAfterMs > 0 ? data.retryAfterMs : false;
    },
  });

// ------------------------------------------------------------ decisions

export const useImportDecisionGroups = (jobId?: string) =>
  useQuery({
    queryKey: ["import", "decisionGroups", jobId],
    queryFn: async () =>
      (await api.get<ImportDecisionGroup[]>(`${baseUrl}/job/${jobId}/decision-groups`))
        .data,
    enabled: !!jobId,
  });

export interface DecisionListParams {
  columnKey?: string;
  state?: ImportDecisionState;
  take?: number;
  skip?: number;
}

export const useImportDecisions = (jobId?: string, params: DecisionListParams = {}) =>
  useQuery({
    queryKey: ["import", "decisions", jobId, params],
    queryFn: async () =>
      (
        await api.get<{ items: ImportDecision[]; total: number }>(
          `${baseUrl}/job/${jobId}/decisions`,
          { params },
        )
      ).data,
    enabled: !!jobId,
    placeholderData: keepPreviousData,
  });

const answerDecisions = async ({
  jobId,
  decisions,
}: {
  jobId: string;
  decisions: ImportDecisionAnswer[];
}) =>
  (
    await api.patch<{ pendingDecisionCount: number; status: string }>(
      `${baseUrl}/job/${jobId}/decisions`,
      { decisions },
    )
  ).data;

export const useAnswerImportDecisions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: answerDecisions,
    onSuccess: (_data, variables) => {
      // Cible les cles de cet import seulement : une invalidation globale
      // rechargerait tout le tableau de bord pour rien.
      queryClient.invalidateQueries({ queryKey: ["import", "decisions", variables.jobId] });
      queryClient.invalidateQueries({
        queryKey: ["import", "decisionGroups", variables.jobId],
      });
      queryClient.invalidateQueries({ queryKey: ["import", "job", variables.jobId] });
    },
  });
};

/** Action de masse, exprimee par predicat plutot que ligne a ligne. */
const applyBulkDecision = async ({
  jobId,
  columnKey,
  scope,
  action,
}: {
  jobId: string;
  columnKey?: string;
  scope: "allAuto" | "allPending" | "allWithoutMatch";
  action: "link" | "skip" | "create";
}) =>
  (
    await api.post<{ affected: number; pendingDecisionCount: number }>(
      `${baseUrl}/job/${jobId}/decisions/bulk`,
      { columnKey, scope, action },
    )
  ).data;

export const useApplyBulkImportDecision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: applyBulkDecision,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["import", "decisions", variables.jobId] });
      queryClient.invalidateQueries({
        queryKey: ["import", "decisionGroups", variables.jobId],
      });
      queryClient.invalidateQueries({ queryKey: ["import", "job", variables.jobId] });
    },
  });
};

// --------------------------------------------------------------- commit

export interface CommitImportDto {
  jobId: string;
  writeMode: ImportWriteMode;
  emptyMeansClear?: boolean;
  /** doit valoir explicitement true : trace de la prise de connaissance */
  confirmDisclaimers: true;
}

const commitImport = async ({ jobId, ...body }: CommitImportDto) =>
  (await api.post<{ jobId: string; status: string }>(
    `${baseUrl}/job/${jobId}/commit`,
    body,
  )).data;

export const useCommitImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: commitImport,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["import", "job", variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ["import", "progress", variables.jobId] });
    },
  });
};

const cancelImport = async (jobId: string) =>
  (await api.post<{ jobId: string; status: string }>(`${baseUrl}/job/${jobId}/cancel`))
    .data;

export const useCancelImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelImport,
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: ["import", "job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["import", "progress", jobId] });
    },
  });
};

// ---------------------------------------------------------------- lignes

export const useImportRows = (
  jobId?: string,
  params: { status?: string; take?: number; skip?: number } = {},
) =>
  useQuery({
    queryKey: ["import", "rows", jobId, params],
    queryFn: async () =>
      (
        await api.get<{ items: ImportRowReport[]; total: number }>(
          `${baseUrl}/job/${jobId}/rows`,
          { params },
        )
      ).data,
    enabled: !!jobId,
    placeholderData: keepPreviousData,
  });

// ------------------------------------------------------------ correspondances

export interface ColumnMapEntry {
  columnIndex: number;
  /** champ cible, ou null pour ignorer explicitement la colonne */
  fieldKey?: string | null;
  lang?: string;
  subKey?: string;
}

/**
 * Enregistre les correspondances de colonnes posées par l'utilisateur : mapper
 * une colonne non reconnue vers un champ, ou l'ignorer. Renvoie le rapport
 * recalculé et le nouveau statut du job.
 */
const updateImportColumns = async ({
  jobId,
  columns,
}: {
  jobId: string;
  columns: ColumnMapEntry[];
}) =>
  (
    await api.patch<{ status: string; report: ImportAnalysisReport }>(
      `${baseUrl}/job/${jobId}/columns`,
      { columns },
    )
  ).data;

export const useUpdateImportColumns = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateImportColumns,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["import", "job", variables.jobId] });
    },
  });
};

// -------------------------------------------------------------- historique

export interface ImportHistoryEntry {
  jobId: string;
  table: ImportTable;
  status: string;
  fileName: string;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  createdAt: string;
  finishedAt: string | null;
  undoneAt: string | null;
  author: string | null;
}

/** Historique des imports Excel de l'instance, paginé, le plus récent d'abord. */
export const useImportHistory = (params: { take?: number; skip?: number } = {}) =>
  useQuery({
    queryKey: ["import", "history", params],
    queryFn: async () =>
      (
        await api.get<{ items: ImportHistoryEntry[]; total: number }>(
          `${baseUrl}/jobs`,
          { params },
        )
      ).data,
    placeholderData: keepPreviousData,
  });

// ------------------------------------------------------------------- undo

export interface UndoImportResult {
  deleted: number;
  notRevertible: number;
  failed: Array<{ entityId: string; message: string }>;
  undone: boolean;
}

/** Annule un import terminé : supprime les fiches créées (pas les mises à jour). */
const undoImport = async (jobId: string) =>
  (await api.post<UndoImportResult>(`${baseUrl}/job/${jobId}/undo`)).data;

export const useUndoImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: undoImport,
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: ["import", "job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["import", "rows", jobId] });
    },
  });
};

// ---------------------------------------------- correspondances sauvegardées

export interface SavedMapping {
  id: string;
  name: string;
  columnCount: number;
  updatedAt: string;
}

/** Correspondances de colonnes enregistrées, réutilisables pour cette table. */
export const useImportMappings = (table?: ImportTable) =>
  useQuery({
    queryKey: ["import", "mappings", table],
    queryFn: async () =>
      (await api.get<SavedMapping[]>(`${baseUrl}/${table}/mappings`)).data,
    enabled: !!table,
  });

const saveImportMapping = async ({ jobId, name }: { jobId: string; name: string }) =>
  (
    await api.post<{ id: string; name: string; columnCount: number }>(
      `${baseUrl}/job/${jobId}/mappings`,
      { name },
    )
  ).data;

export const useSaveImportMapping = (table?: ImportTable) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveImportMapping,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["import", "mappings", table] }),
  });
};

const applyImportMapping = async ({
  jobId,
  mappingId,
}: {
  jobId: string;
  mappingId: string;
}) =>
  (
    await api.post<{
      /** entrées à pré-remplir dans les dropdowns « Rattacher à » (pas persisté) */
      entries: ColumnMapEntry[];
      applied: number;
      name: string;
    }>(`${baseUrl}/job/${jobId}/mappings/${mappingId}/apply`)
  ).data;

export const useApplyImportMapping = () =>
  useMutation({ mutationFn: applyImportMapping });

const deleteImportMapping = async ({
  table,
  mappingId,
}: {
  table: ImportTable;
  mappingId: string;
}) => (await api.delete(`${baseUrl}/${table}/mappings/${mappingId}`)).data;

export const useDeleteImportMapping = (table?: ImportTable) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImportMapping,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["import", "mappings", table] }),
  });
};

/**
 * Telecharge le fichier de rejets : les lignes en echec, reprises avec les
 * colonnes du client plus une colonne « Erreur », a corriger et re-televerser.
 */
const downloadRejects = (jobId: string) =>
  api.get(`${baseUrl}/job/${jobId}/rejects`, { responseType: "blob" });

export const useDownloadImportRejects = () =>
  useMutation({ mutationFn: downloadRejects });

/** Quand l'ecriture est finie, rafraichir les listes de fiches concernees. */
export function invalidateAfterImport(
  queryClient: ReturnType<typeof useQueryClient>,
  table: ImportTable,
): void {
  for (const key of [[table], ["countAllItems"], ["countByStatus"], ["not-related"]]) {
    queryClient.invalidateQueries({ queryKey: key });
  }
}

export { isTerminal };
